import { useState } from "react";
import { motion } from "framer-motion";
import { X, Loader2, CreditCard, Truck } from "lucide-react";
import { toast } from "sonner";
import { useT } from "../lib/i18n";
import { useFormatEUR } from "../lib/format";

const COUNTRY_KEYS = [
  "country.france", "country.belgium", "country.switzerland", "country.luxembourg",
  "country.germany", "country.spain", "country.italy", "country.netherlands",
  "country.uk", "country.other",
];

export const OrderModal = ({ product, size, paper, frame, total, onClose }) => {
  const t = useT();
  const formatEUR = useFormatEUR();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "",
    street: "", postal_code: "", city: "", country: t("country.france"),
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const resp = await fetch(`${API}/api/checkout/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          product_id: product.id,
          product_title: product.title,
          size_key: size.key,
          paper_key: paper.key,
          frame_key: frame?.key || "standard",
          quantity: 1,
          shipping: form,
          origin_url: window.location.origin,
        }),
      });
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(txt || t("order.err.payment"));
      }
      const data = await resp.json();
      if (data.url) window.location.href = data.url;
      else throw new Error(t("order.err.noUrl"));
    } catch (err) {
      toast.error(err.message || t("order.err.default"));
      setSubmitting(false);
    }
  };

  return (
    <motion.div
      data-testid="order-modal"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[120] bg-ink/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-8 overflow-y-auto"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        className="bg-bone text-ink w-full max-w-xl rounded-md shadow-2xl my-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-ink/10">
          <div className="flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase">
            <Truck size={12} /> {t("order.header")}
          </div>
          <button data-testid="order-close" onClick={onClose} className="text-ink/60 hover:text-ink p-1">
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-5 bg-ink/3 border-b border-ink/10">
          <div className="text-[10px] tracking-[0.22em] uppercase text-ink/55 mb-1">{t("order.summary")}</div>
          <div className="font-display text-2xl leading-tight">{product.title}</div>
          <div className="text-sm text-ink/65 mt-1">
            {size.label} · {paper.label} · {t("order.frame")} {frame?.label || t("order.frameStd")}
          </div>
          <div className="font-display text-3xl mt-3">{formatEUR(total)}</div>
          <div className="text-[10px] tracking-[0.18em] uppercase text-ink/45 mt-1">{t("order.taxIncl")}</div>
        </div>

        <form onSubmit={submit} className="px-6 py-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label={t("order.fullName")} name="full_name" value={form.full_name} on={update} required testid="order-name" />
            <Field label={t("order.emailLabel")} name="email" type="email" value={form.email} on={update} required testid="order-email" />
          </div>
          <Field label={t("order.phone")} name="phone" value={form.phone} on={update} required testid="order-phone" />
          <Field label={t("order.street")} name="street" value={form.street} on={update} required testid="order-street" />
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("order.postal")} name="postal_code" value={form.postal_code} on={update} required testid="order-cp" />
            <Field label={t("order.city")} name="city" value={form.city} on={update} required testid="order-city" />
          </div>
          <div>
            <label className="block text-[10px] tracking-[0.22em] uppercase text-ink/55 mb-1.5">{t("order.country")}</label>
            <select
              data-testid="order-country"
              value={form.country} onChange={(e) => update("country", e.target.value)}
              className="w-full border border-ink/20 px-3 py-2.5 bg-bone text-ink text-sm focus:outline-none focus:border-ink"
            >
              {COUNTRY_KEYS.map((k) => {
                const label = t(k);
                return <option key={k} value={label}>{label}</option>;
              })}
            </select>
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="order-submit"
            className="w-full mt-2 bg-ink text-bone hover:bg-ink/90 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 px-5 py-3.5 text-[11px] tracking-[0.28em] uppercase font-medium transition-all"
          >
            {submitting ? <><Loader2 size={14} className="animate-spin" /> {t("order.redirecting")}</> :
              <><CreditCard size={14} /> {t("order.pay")} {formatEUR(total)}</>}
          </button>
          <p className="text-[10px] tracking-[0.06em] text-ink/45 leading-relaxed mt-3">
            {t("order.securedBy")}
          </p>
        </form>
      </motion.div>
    </motion.div>
  );
};

const Field = ({ label, name, value, on, type = "text", required, testid }) => (
  <div>
    <label className="block text-[10px] tracking-[0.22em] uppercase text-ink/55 mb-1.5">{label}</label>
    <input
      data-testid={testid}
      type={type} value={value} onChange={(e) => on(name, e.target.value)} required={required}
      className="w-full border border-ink/20 px-3 py-2.5 bg-bone text-ink text-sm focus:outline-none focus:border-ink"
    />
  </div>
);

export default OrderModal;
